import { useState, useEffect, useCallback } from "react";
import {
  Dialog, DialogContent, Box, Typography, Button,
  TextField, CircularProgress, Alert, IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SendIcon from "@mui/icons-material/Send";
import RefreshIcon from "@mui/icons-material/Refresh";
import { APIURL } from "../../globals";

const GOLD      = "#D97706";
const GOLD_GRAD = "linear-gradient(135deg,#D97706,#F59E0B)";

const FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    fontFamily: "IBM Plex Sans, sans-serif",
    fontSize: 14,
    color: "#0F172A",
    background: "#F8FAFC",
    borderRadius: "10px",
    "& fieldset": { borderColor: "#E2E8F0" },
    "&:hover fieldset": { borderColor: "#94A3B8" },
    "&.Mui-focused fieldset": { borderColor: GOLD, borderWidth: "1px" },
  },
  "& .MuiInputLabel-root": {
    fontFamily: "IBM Plex Sans, sans-serif",
    fontSize: 14,
    color: "#64748B",
    "&.Mui-focused": { color: GOLD },
  },
};

function newChallenge() {
  const a = Math.floor(Math.random() * 9) + 1;
  const b = Math.floor(Math.random() * 9) + 1;
  return { a, b, answer: a + b };
}

export default function ContactModal({ open, onClose }) {
  const [form,      setForm]      = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const [captcha,   setCaptcha]   = useState(newChallenge);
  const [userAns,   setUserAns]   = useState("");
  const [ansError,  setAnsError]  = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [success,   setSuccess]   = useState(false);
  const [error,     setError]     = useState("");

  const refreshCaptcha = useCallback(() => {
    setCaptcha(newChallenge());
    setUserAns("");
    setAnsError(false);
  }, []);

  useEffect(() => {
    if (open) {
      setSuccess(false);
      setError("");
      setForm({ name: "", email: "", company: "", phone: "", message: "" });
      refreshCaptcha();
    }
  }, [open, refreshCaptcha]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAnsError(false);
    setError("");

    if (parseInt(userAns, 10) !== captcha.answer) {
      setAnsError(true);
      refreshCaptcha();
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${APIURL}/general/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Something went wrong.");
      }
      setSuccess(true);
    } catch (err) {
      setError(err.message || "Failed to send. Please try again.");
      refreshCaptcha();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          border: "1px solid #E2E8F0",
          boxShadow: "0 24px 80px rgba(15,23,42,.18)",
          overflow: "hidden",
        },
      }}
    >
      {/* Header bar */}
      <Box sx={{
        background: "#0F172A",
        px: 3.5, py: 2.5,
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box component="img" src="/logo.png" alt="AMLDiligence" sx={{ height: 30, display: "block", filter: "brightness(0) invert(1)" }} />
          <Box sx={{ width: 1, height: 28, background: "rgba(255,255,255,.12)" }} />
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#94A3B8", fontFamily: "IBM Plex Sans, sans-serif" }}>
            Request a Demo
          </Typography>
        </Box>
        <IconButton aria-label="Close" onClick={onClose} sx={{ color: "#475569", "&:hover": { color: "#F8FAFC", background: "rgba(255,255,255,.07)" } }}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3.5, background: "#fff" }}>
        {success ? (
          /* Success state */
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Box sx={{ width: 64, height: 64, borderRadius: "50%", background: "rgba(22,163,74,.1)", border: "1px solid rgba(22,163,74,.2)", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2.5 }}>
              <CheckCircleIcon sx={{ fontSize: 32, color: "#16A34A" }} />
            </Box>
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: "#0F172A", mb: 1, fontFamily: "IBM Plex Sans, sans-serif" }}>
              Message sent!
            </Typography>
            <Typography sx={{ fontSize: 14, color: "#64748B", mb: 3.5, fontFamily: "IBM Plex Sans, sans-serif" }}>
              Thank you! Our team will be in touch within one business day.
            </Typography>
            <Button onClick={onClose} sx={{ px: 4, py: 1.25, borderRadius: "10px", fontFamily: "IBM Plex Sans, sans-serif", fontWeight: 600, textTransform: "none", background: GOLD_GRAD, color: "#fff", "&:hover": { background: "linear-gradient(135deg,#B45309,#D97706)" } }}>
              Close
            </Button>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit}>
            {error && (
              <Alert severity="error" sx={{ mb: 2.5, borderRadius: "10px", fontFamily: "IBM Plex Sans, sans-serif" }}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 2 }}>
              <TextField label="Full name *" value={form.name}    onChange={set("name")}    required size="small" fullWidth sx={FIELD_SX} />
              <TextField label="Email *"     value={form.email}   onChange={set("email")}   required size="small" fullWidth sx={FIELD_SX} type="email" />
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 2 }}>
              <TextField label="Company"  value={form.company} onChange={set("company")} size="small" fullWidth sx={FIELD_SX} />
              <TextField label="Phone"    value={form.phone}   onChange={set("phone")}   size="small" fullWidth sx={FIELD_SX} type="tel" />
            </Box>
            <TextField
              label="How can we help? *"
              value={form.message}
              onChange={set("message")}
              required multiline rows={3}
              fullWidth sx={{ ...FIELD_SX, mb: 2 }}
            />

            {/* Math captcha */}
            <Box sx={{
              display: "flex", alignItems: "center", gap: 2,
              p: 2, borderRadius: "12px",
              background: "#F8FAFC", border: `1px solid ${ansError ? "#DC2626" : "#E2E8F0"}`,
              mb: ansError ? 1 : 2.5,
            }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#64748B", mb: .75, fontFamily: "IBM Plex Sans, sans-serif", textTransform: "uppercase", letterSpacing: "1px" }}>
                  Spam check
                </Typography>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#0F172A", fontFamily: "IBM Plex Sans, sans-serif" }}>
                  What is {captcha.a} + {captcha.b}?
                </Typography>
              </Box>
              <TextField
                value={userAns}
                onChange={(e) => { setUserAns(e.target.value); setAnsError(false); }}
                required
                size="small"
                type="number"
                placeholder="?"
                inputProps={{ min: 1, max: 99, style: { textAlign: "center", fontFamily: "IBM Plex Sans, sans-serif", fontWeight: 700, fontSize: 18, width: 56 } }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    background: "#fff",
                    "& fieldset": { borderColor: ansError ? "#DC2626" : "#E2E8F0" },
                    "&.Mui-focused fieldset": { borderColor: ansError ? "#DC2626" : GOLD },
                  },
                }}
              />
              <IconButton
                type="button"
                onClick={refreshCaptcha}
                aria-label="New question"
                size="small"
                sx={{ color: "#94A3B8", "&:hover": { color: GOLD, background: "rgba(217,119,6,.06)" } }}
              >
                <RefreshIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
            {ansError && (
              <Typography sx={{ fontSize: 12, color: "#DC2626", mb: 2, fontFamily: "IBM Plex Sans, sans-serif", pl: .5 }}>
                Incorrect answer — a new question has been generated. Please try again.
              </Typography>
            )}

            <Button
              type="submit"
              disabled={loading}
              endIcon={loading ? <CircularProgress size={15} color="inherit" /> : <SendIcon sx={{ fontSize: 16 }} />}
              fullWidth
              sx={{
                py: 1.6, borderRadius: "10px",
                fontSize: 15, fontWeight: 600, textTransform: "none",
                fontFamily: "IBM Plex Sans, sans-serif",
                background: GOLD_GRAD, color: "#fff",
                boxShadow: "0 6px 20px rgba(217,119,6,.3)",
                "&:hover": { background: "linear-gradient(135deg,#B45309,#D97706)", boxShadow: "0 10px 28px rgba(217,119,6,.45)" },
                "&.Mui-disabled": { background: "rgba(217,119,6,.25)", color: "rgba(255,255,255,.5)" },
                transition: "all .22s",
              }}
            >
              {loading ? "Sending…" : "Send Message"}
            </Button>

            <Typography sx={{ fontSize: 11, color: "#94A3B8", mt: 1.5, textAlign: "center", fontFamily: "IBM Plex Sans, sans-serif" }}>
              We respond within 1 business day. No spam, ever.
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
